/**
 * Definition of all "app-configurations" that are used for the Memory Parser.
 * @note We may consider moving this to a database/CMS-type system in the future.
 */

import type { MemoryToolConfig } from '@timelyapp/integration-cloud-api-spec';
import assets from '@timelyapp/static-assets';

export const toolConfigurations: Record<string, MemoryToolConfig> = {
  amazon: {
    tool: {
      title: 'Amazon',
      domains: ['amazon.com', 'amazon.com.au', 'm.media-amazon.com'],
      category: 'ecommerce',
      color: 'rgba(255,153,0,0.30)',
    },
    patterns: [
      {
        in: 'title',
        pattern: '^(.+?)(?:\\s*:\\s*.+)?(?:\\s*—\\s*.+)?$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
    ],
  },
  aws: {
    tool: {
      title: 'Amazon Web Services',
      domains: [
        'aws.amazon.com',
        'console.aws.amazon.com',
        'docs.aws.amazon.com',
        'iq.aws.amazon.com',
      ],
      category: 'developer_tools',
      color: 'rgba(255,153,0,0.30)',
    },
    patterns: [
      {
        in: 'title',
        pattern: '^(.+?)(?:\\s*\\|\\s*.+)?$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
    ],
  },
  autodesk_revit: {
    tool: {
      title: 'Autodesk Revit',
      aliases: ['Revit'],
      color: 'rgba(0,72,167,0.30)',
      category: 'design',
    },
    patterns: [
      {
        in: 'title',
        pattern:
          'Autodesk Revit (\\d{4}(?:\\.\\d+)?) - \\[(.+?\\.rvt) - (.+?): (.+?)\\](?: \\(Not Responding\\))?$',
        mapsTo: ['version', 'file', 'viewType', 'view'],
        niceTitle: '{viewType}: {view}',
        context: '{file}',
      },
      {
        in: 'title',
        pattern: 'Autodesk Revit (\\d{4}(?:\\.\\d+)?) - \\[(.+?)\\](?: \\(Not Responding\\))?$',
        mapsTo: ['version', 'view'],
        niceTitle: '{view}',
      },
      {
        in: 'title',
        pattern: 'Autodesk Revit (\\d{4}(?:\\.\\d+)?)(?: \\(Not Responding\\))?$',
        mapsTo: ['version'],
        niceTitle: 'Autodesk Revit {version}',
      },
    ],
  },
  hubspot: {
    tool: {
      title: 'HubSpot',
      aliases: ['HS'],
      color: 'rgba(255,155,43,0.30)',
      domains: ['app.hubspot.com'],
      icon: 'https://icons.timelyapp.com/favicons/app.hubspot.com',
      category: 'sales',
    },
  },
  salesforce: {
    tool: {
      title: 'Salesforce',
      domains: [
        'salesforce.com',
        '*.salesforce.com',
        '*.lightning.force.com',
        '*.my.salesforce.com',
        '*.force.com',
      ],
      icon: 'salesforce.png',
      category: 'sales',
    },
    patterns: [
      {
        // Strip notification count prefix: "(3) Chatter | Salesforce" → "Chatter"
        in: 'title',
        pattern: '^\\(\\d+\\)\\s+(.+?)\\s*\\|\\s*Salesforce.*$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
      {
        // Lightning format: "Record Name | Salesforce" → "Record Name"
        in: 'title',
        pattern: '^(.+?)\\s*\\|\\s*Salesforce.*$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
      {
        // Classic format: "Account: Acme Corp ~ Salesforce - Developer Edition" → "Account: Acme Corp"
        in: 'title',
        pattern: '^(.+?)\\s*~\\s*Salesforce.*$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
    ],
  },
  intercom: {
    tool: {
      title: 'Intercom',
      color: 'rgba(26, 145, 243, 0.30)',
      domains: ['app.intercom.com'],
      category: 'support',
    },
    patterns: [
      {
        in: 'title',
        pattern: '^(.+?) \\| (?:.+? \\| )?(.+?) \\| Intercom$',
        mapsTo: ['title', 'company'],
        niceTitle: '{title}',
        context: '{company}',
      },
      {
        in: 'title',
        pattern: '^(.+?) \\| Intercom$',
        mapsTo: ['title'],
        niceTitle: '{title}',
        context: undefined,
      },
    ],
  },
  chartmogul: {
    tool: {
      title: 'ChartMogul',
      color: 'rgba(255,155,43,0.30)',
      domains: ['app.chartmogul.com'],
      icon: 'https://icons.timelyapp.com/favicons/app.chartmogul.com',
      category: 'analytics',
    },
  },
  linkedin: {
    tool: {
      title: 'LinkedIn',
      color: 'rgba(255,155,43,0.30)',
      domains: ['linkedin.com'],
      icon: 'https://icons.timelyapp.com/favicons/linkedin.com',
      category: 'social',
    },
  },
  amplitude: {
    tool: {
      title: 'Amplitude',
      color: 'rgba(255,155,43,0.30)',
      domains: ['app.amplitude.com'],
      icon: 'https://icons.timelyapp.com/favicons/amplitude.com',
      category: 'analytics',
    },
  },
  jiminny: {
    tool: {
      title: 'Jiminny',
      color: 'rgba(255,155,43,0.30)',
      domains: ['app.jiminny.com', 'app.jiminny.eu'],
      icon: 'https://icons.timelyapp.com/favicons/app.jiminny.com',
      category: 'sales',
    },
  },
  netflix: {
    tool: {
      title: 'Netflix',
      color: 'rgba(255,155,43,0.30)',
      domains: ['netflix.com'],
      icon: 'https://icons.timelyapp.com/favicons/netflix.com',
      category: 'video',
    },
  },
  facebook: {
    tool: {
      title: 'Facebook',
      color: 'rgba(255,155,43,0.30)',
      domains: ['facebook.com'],
      icon: 'https://icons.timelyapp.com/favicons/facebook.com',
      category: 'social',
    },
  },

  facebook_ads_manager: {
    tool: {
      title: 'Facebook Ads Manager',
      domains: ['adsmanager.facebook.com'],
      category: 'sales',
      color: 'rgba(66,103,178,0.30)',
    },
    patterns: [
      {
        in: 'title',
        pattern:
          '^\\(\\d+\\)\\s+(?:Ads Manager\\s*-\\s*)?(.+?)(?:\\s+[-–]\\s+(?:Google Chrome|Brave))?$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
    ],
  },
  whatsapp: {
    tool: {
      title: 'WhatsApp',
      color: 'rgba(255,155,43,0.30)',
      domains: ['web.whatsapp.com'],
      icon: 'https://icons.timelyapp.com/favicons/web.whatsapp.com',
      category: 'communication',
      coerceTitle: true, // TODO: Remove + revisit pattern after we have better understanding of Whatsapp on web
    },

    // patterns: [
    //   {
    //     in: 'title',
    //     pattern: '^\\((\\d+)\\)\\s+WhatsApp(?:\\s+-\\s+(.+?))?$',
    //     mapsTo: ['unread_count', 'context'],
    //     niceTitle: '{context}',
    //   },
    // ],
  },
  productlane: {
    tool: {
      title: 'ProductLane',
      color: 'rgba(255,155,43,0.30)',
      domains: ['productlane.com'],
      icon: 'https://icons.timelyapp.com/favicons/productlane.com',
      category: 'product',
    },
  },
  slite: {
    tool: {
      title: 'Slite',
      color: 'rgba(255,155,43,0.30)',
      domains: ['*.slite.com'],
      icon: 'https://icons.timelyapp.com/favicons/slite.com',
      category: 'knowledge_base',
    },
  },
  supabase: {
    tool: {
      title: 'Supabase',
      color: 'rgba(255,155,43,0.30)',
      domains: ['app.supabase.com'],
      icon: 'https://icons.timelyapp.com/favicons/app.supabase.com',
      category: 'developer_tools',
    },
  },
  pandadoc: {
    tool: {
      title: 'PandaDoc',
      color: 'rgba(255,155,43,0.30)',
      domains: ['app.pandadoc.com'],
      icon: 'https://icons.timelyapp.com/favicons/app.pandadoc.com',
      category: 'legal',
    },
  },
  sentry: {
    tool: {
      title: 'Sentry',
      color: 'rgba(255,155,43,0.30)',
      domains: ['*.sentry.io'],
      icon: 'https://icons.timelyapp.com/favicons/sentry.io',
      category: 'developer_tools',
    },
  },
  clickup: {
    tool: {
      title: 'ClickUp',
      color: 'rgba(255,155,43,0.30)',
      domains: ['app.clickup.com'],
      icon: 'https://icons.timelyapp.com/favicons/app.clickup.com',
      category: 'project_management',
    },
  },
  slab: {
    tool: {
      title: 'Slab',
      color: 'rgba(255,155,43,0.30)',
      domains: ['*.slab.com'],
      icon: 'https://icons.timelyapp.com/favicons/slab.com',
      category: 'knowledge_base',
    },
  },
  stripe: {
    tool: {
      title: 'Stripe',
      color: 'rgba(255,155,43,0.30)',
      domains: ['dashboard.stripe.com'],
      icon: 'https://icons.timelyapp.com/favicons/dashboard.stripe.com',
      category: 'payment',
    },
  },
  aircall: {
    tool: {
      title: 'Aircall',
      color: 'rgba(255,155,43,0.30)',
      domains: ['app.aircall.io'],
      icon: 'https://icons.timelyapp.com/favicons/app.aircall.io',
      category: 'communication',
    },
  },
  arc: {
    tool: {
      title: 'Arc',
      color: 'rgba(49,57,251,0.30)',
      icon: 'https://icons.timelyapp.com/favicons/arc.net',
      category: 'browser',
    },
  },
  asana: {
    tool: {
      title: 'Asana',
      color: 'rgba(255,155,43,0.30)',
      domains: ['app.asana.com'],
      category: 'project_management',
      icon: 'https://icons.timelyapp.com/favicons/asana.com',
    },
    patterns: [
      // Title patterns - ordered most to least specific
      // All patterns strip leading ● bullet indicator from Asana UI
      {
        // Webhook-generated: "Verb task: Task Name"
        in: 'title',
        pattern:
          '^●?\\s*(?:Assigned|Created|Renamed|Completed|Added|Removed|Changed|Updated)\\s+task:\\s*(.+)$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
      {
        // "Task Name - Asana" - must come before Project-Task pattern
        in: 'title',
        pattern: '^●?\\s*(.+?)\\s*[-–]\\s*Asana$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
      {
        // "Project Name - Task Name" - requires space around separator to avoid "Cross-functional"
        in: 'title',
        pattern: '^●?\\s*(.+?)\\s+[-–]\\s+(.+?)\\s*$',
        mapsTo: ['projectName', 'content'],
        niceTitle: '{content}',
        context: '{projectName}',
      },
      {
        // Standalone bullet-prefixed title (strip bullet only)
        in: 'title',
        pattern: '^●\\s*(.+?)\\s*$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },

      // URL patterns - new format (/1/) introduced ~2024
      {
        // Task with project context: /1/{workspaceGid}/project/{projectGid}/task/{taskGid}
        in: 'url',
        pattern: 'https://app\\.asana\\.com/1/(\\d+)/project/(\\d+)/task/(\\d+)',
        mapsTo: ['workspaceGid', 'projectGid', 'taskGid'],
        uri: 'tic://synthetic/asana/tasks/{taskGid}',
        parentUri: 'tic://synthetic/asana/projects/{projectGid}',
        level: 'task',
      },
      {
        // Task without project context: /1/{workspaceGid}/task/{taskGid}
        in: 'url',
        pattern: 'https://app\\.asana\\.com/1/(\\d+)/task/(\\d+)',
        mapsTo: ['workspaceGid', 'taskGid'],
        uri: 'tic://synthetic/asana/tasks/{taskGid}',
        level: 'task',
      },
      {
        // Project board view: /1/{workspaceGid}/project/{projectGid}/board/{boardGid}
        in: 'url',
        pattern: 'https://app\\.asana\\.com/1/(\\d+)/project/(\\d+)/board/(\\d+)',
        mapsTo: ['workspaceGid', 'projectGid', 'boardGid'],
        uri: 'tic://synthetic/asana/projects/{projectGid}',
        level: 'project',
      },
      {
        // Project with view: /1/{workspaceGid}/project/{projectGid}/list|overview|...
        in: 'url',
        pattern:
          'https://app\\.asana\\.com/1/(\\d+)/project/(\\d+)(?:/(?:list|timeline|calendar|overview|files|forms)(?:/\\d+)?)?$',
        mapsTo: ['workspaceGid', 'projectGid'],
        uri: 'tic://synthetic/asana/projects/{projectGid}',
        level: 'project',
      },

      // URL patterns - old format (/0/) for backwards compatibility
      // More specific /0/0/ pattern must come before general /0/{id}/
      {
        // Task direct link (no project context): /0/0/{taskGid}[/f]
        in: 'url',
        pattern: 'https://app\\.asana\\.com/0/0/(\\d+)(?:/f)?$',
        mapsTo: ['taskGid'],
        uri: 'tic://synthetic/asana/tasks/{taskGid}',
        level: 'task',
      },
      {
        // Task with project context: /0/{projectGid}/{taskGid}[/f]
        in: 'url',
        pattern: 'https://app\\.asana\\.com/0/(\\d+)/(\\d+)(?:/f)?$',
        mapsTo: ['projectGid', 'taskGid'],
        uri: 'tic://synthetic/asana/tasks/{taskGid}',
        parentUri: 'tic://synthetic/asana/projects/{projectGid}',
        level: 'task',
      },
      {
        // Project view: /0/{projectGid}[/view][?params]
        in: 'url',
        pattern:
          'https://app\\.asana\\.com/0/(\\d+)(?:/(?:list|board|timeline|calendar|overview|files|forms))?(?:\\?.*)?$',
        mapsTo: ['projectGid'],
        uri: 'tic://synthetic/asana/projects/{projectGid}',
        level: 'project',
      },
    ],
  },
  location_tracker: {
    tool: {
      title: 'Location Tracker',
      color: 'rgba(255,155,43,0.30)',
      icon: 'gps_tracker.png',
      category: 'location_tracking',
    },
  },
  github: {
    tool: {
      title: 'GitHub',
      aliases: ['Github Enterprise'],
      domains: ['github.com'],
      category: 'developer_tools',
      color: 'rgba(52,166,255,0.30)',
    },
    patterns: [
      {
        in: 'title',
        pattern: '(.+) · Pull Request #([0-9]+) · (.+)/(.+)',
        mapsTo: ['prTitle', 'prNumber', 'owner', 'repo'],
        niceTitle: '{prTitle} ({owner}/{repo}#{prNumber})',
        context: '{owner}/{repo}',
      },
      {
        in: 'url',
        pattern: 'https://github.com/(.+)/(.+)/pull/([0-9]+)',
        mapsTo: ['owner', 'repo', 'prNumber'],
        uri: 'https://api.github.com/repos/{owner}/{repo}/pulls/{prNumber}',
      },
      {
        in: 'title',
        pattern: '(.+) · Issue #([0-9]+) · (.+?)/(.+)',
        mapsTo: ['issueTitle', 'issueNumber', 'owner', 'repo'],
        niceTitle: '{issueTitle} ({owner}/{repo}#{issueNumber})',
        context: '{owner}/{repo}',
      },
      {
        in: 'url',
        pattern: 'https://github\\.com/(.+?)/(.+?)/issues/([0-9]+)',
        mapsTo: ['owner', 'repo', 'issueNumber'],
        uri: 'https://api.github.com/repos/{owner}/{repo}/issues/{issueNumber}',
      },
      {
        in: 'url',
        pattern: '^https://github\\.com/([^/]+)/([^/]+)(/.*|$)',
        mapsTo: ['owner', 'repo'],
        uri: 'https://api.github.com/repos/{owner}/{repo}',
      },
    ],
  },
  google_ads: {
    tool: {
      title: 'Google Ads',
      domains: ['ads.google.com'],
      category: 'sales',
      color: 'rgba(52,168,83,0.30)',
    },
    patterns: [
      {
        in: 'title',
        pattern: '^(.+) - Google Ads$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
      {
        in: 'title',
        pattern:
          '^(.+) - (.+) - Google Ads(?:\\s*-\\s*(?:High memory usage - [\\d.]+ [GM]B|Google Chrome)(?:\\s*-\\s*(.+))?)*$',
        mapsTo: ['content', 'account', 'user'],
        niceTitle: '{content} - {account}',
      },
    ],
  },
  slack: {
    tool: {
      title: 'Slack',
      domains: ['app.slack.com'],
      color: 'rgba(255,123,49,0.30)',
      icon: 'slack.png',
      category: 'communication',
    },
    patterns: [
      {
        in: 'title',
        pattern: '^Slack - (.+?) - (.+?) - (\\d+ new items?)$', // new format: catches unread
        mapsTo: ['channel', 'team', 'unread'],
        niceTitle: '{channel}',
        context: '{team}',
      },
      {
        in: 'title',
        pattern: '^Slack - (.+) - (.+)$', // new format: catches unread
        mapsTo: ['channel', 'team'],
        niceTitle: '{channel}',
        context: '{team}',
      },
      {
        in: 'title',
        pattern: '^Slack - (.+)', // new format, without unreads
        mapsTo: ['channel', 'team'],
        niceTitle: '{channel}',
        context: '{team}',
      },
      {
        in: 'title',
        pattern: '^!?\\s*(.+?) \\((.+?)\\) - (.+?)(?: - (\\d+ new items?))?( - Slack.*)?$',
        mapsTo: ['name', 'type', 'team', 'unread', 'suffix'],
        niceTitle: '{name} ({type})',
        context: '{team}',
      },
      {
        in: 'title',
        pattern: '^!?\\s*(.+?) - (.+?)(?: - (\\d+ new items?))?( - Slack.*)?$',
        mapsTo: ['channel', 'team', 'unread', 'suffix'],
        niceTitle: '{channel}',
        context: '{team}',
      },
      {
        in: 'title',
        pattern: '^Huddle: (.+) - (.+?) - Slack.*$',
        mapsTo: ['huddle', 'team'],
        niceTitle: 'Huddle: {huddle}',
        context: '{team}',
      },
      {
        in: 'title',
        pattern:
          '^(Search|Threads|Activity|Later|New message) - (.+?)(?: - (\\d+ new items?))?( - Slack.*)?$',
        mapsTo: ['section', 'team', 'unread', 'suffix'],
        niceTitle: '{section}',
        context: '{team}',
      },
      {
        in: 'title',
        pattern: '^Slack$',
        mapsTo: [],
        niceTitle: 'Slack',
      },
    ],
  },
  gmail: {
    tool: {
      title: 'Gmail',
      domains: ['mail.google.com'],
      category: 'communication',
      cleanUrls: false,
      icon: `https://icons.timelyapp.com/favicons/mail.google.com`,
    },
    patterns: [
      {
        in: 'title',
        pattern: 'Inbox \\(([0-9,]+)\\) - (.+) - (.+)',
        mapsTo: ['unread', 'email'],
        niceTitle: 'Inbox - {email}',
      },
      {
        in: 'title',
        pattern: '^(.+) - (.+) - .+$',
        mapsTo: ['subject', 'email'],
        niceTitle: '{subject} - {email}',
      },
    ],
  },
  '1password': {
    tool: {
      title: '1Password',
      domains: ['1password.com'],
      color: 'rgba(0,0,0,0.30)',
      icon: '1password.png',
      category: 'utilities',
    },
  },
  sketchbook: {
    tool: {
      title: 'SketchBook',
      color: 'rgba(230,103,30,0.30)',
      icon: 'sketchbook.png',
    },
  },
  sketchup: {
    tool: {
      title: 'SketchUp',
      color: 'rgba(255,0,0,0.30)',
      icon: 'sketchup.png',
      category: 'design',
    },
  },
  sketch: {
    tool: {
      title: 'Sketch',
      color: 'rgba(253,251,118,0.30)',
      icon: 'sketch.png',
      category: 'design',
    },
  },
  photoshop: {
    tool: {
      title: 'Photoshop',
      aliases: ['Photoshop CC', 'Adobe Photoshop', 'Adobe Photoshop CC'],
      color: 'rgba(52,166,255,0.30)',
      icon: 'adobe_photoshop.png',
      category: 'design',
    },
  },
  pages: {
    tool: {
      title: 'Pages',
      color: 'rgba(255,155,43,0.30)',
      icon: 'pages.png',
      category: 'word_processing',
    },
  },
  numbers: {
    tool: {
      title: 'Numbers',
      color: 'rgba(137,202,38,0.30)',
      icon: 'numbers.png',
      category: 'productivity',
    },
  },
  keynote: {
    tool: {
      title: 'Keynote',
      color: 'rgba(97,165,255,0.30)',
      icon: 'keynote.png',
      category: 'presentation',
    },
  },
  microsoft_word: {
    tool: {
      title: 'Microsoft Word',
      aliases: ['Word'],
      color: 'rgba(255,155,43,0.30)',
      icon: 'word.png',
      category: 'word_processing',
    },
  },
  microsoft_powerpoint: {
    tool: {
      title: 'Microsoft Powerpoint',
      aliases: ['Powerpoint'],
      color: 'rgba(210,71,38,0.30)',
      icon: 'powerpoint.png',
      category: 'presentation',
    },
    patterns: [
      {
        in: 'title',
        pattern:
          '^(.+?)(?:\\s*-\\s*PowerPoint)?(?:\\s*-\\s*(Read-Only|Protected View|Compatibility Mode|Repaired))?(?:\\s*-\\s*PowerPoint(?:\\s*\\(Product Activation Failed\\))?)?$',
        mapsTo: ['file', 'mode'],
        niceTitle: '{file}',
      },
      {
        in: 'title',
        pattern: '^(.+?\\.pptx?)(?:\\s*Properties)?$',
        mapsTo: ['file'],
        niceTitle: '{file}',
      },
    ],
  },
  microsoft_excel: {
    tool: {
      title: 'Microsoft Excel',
      aliases: ['Excel'],
      color: 'rgba(137,202,38,0.30)',
      icon: 'excel.png',
      category: 'spreadsheet',
    },
  },
  microsoft_outlook: {
    tool: {
      title: 'Microsoft Outlook',
      aliases: ['Outlook'],
      domains: [
        'outlook.com',
        'outlook.live.com',
        'outlook.office.com',
        'outlook.office365.com',
        '*.protection.outlook.com',
      ],
      color: 'rgba(52,166,255,0.30)',
      icon: 'outlook.png',
      category: 'communication',
    },
    patterns: [
      {
        in: 'title',
        pattern: '^(.+) - (.+@.+) - Outlook$',
        mapsTo: ['subject', 'email'],
        niceTitle: '{subject} - {email}',
      },
      {
        in: 'title',
        pattern: '^(.+) - Outlook$',
        mapsTo: ['subject'],
        niceTitle: '{subject}',
      },
    ],
  },
  microsoft_outlook_calendar: {
    tool: {
      title: 'O365 Calendar',
      color: 'rgba(52,166,255,0.30)',
      icon: 'office365.png',
      category: 'calendar',
    },
  },
  microsoft_teams: {
    tool: {
      title: 'Microsoft Teams',
      aliases: ['Teams', 'Microsoft Teams classic', 'Microsoft Teams (work or school)'],
      color: 'rgba(245,179,137,0.30)',
      icon: 'microsoft_teams.png',
      category: 'communication',
    },
  },
  microsoft_onenote: {
    tool: {
      title: 'Microsoft OneNote',
      aliases: ['OneNote'],
      color: 'rgba(80,61,154,0.30)',
      category: 'productivity',
      coerceTitle: true,
    },
    patterns: [
      {
        in: 'title',
        pattern: '^(.+?)(?:\\s+-\\s+OneNote(?:\\s+\\(Unlicensed Product\\))?)?$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
    ],
  },
  vlc: {
    tool: {
      title: 'VLC',
      icon: 'vlc.png',
      category: 'video',
    },
  },
  quicktime: {
    tool: {
      title: 'Quicktime',
      aliases: ['Quicktime Player'],
      category: 'video',
    },
  },
  pixelmator: {
    tool: {
      title: 'Pixelmator',
      color: 'rgba(253,251,118,0.30)',
      icon: 'pixelmator.png',
      category: 'media_editing',
    },
  },
  google_search: {
    tool: {
      title: 'Google',
      domains: ['google.com'],
      color: 'rgba(248,81,110,0.30)',
      icon: 'https://icons.timelyapp.com/favicons/google.com',
      category: 'calendar',
    },
  },
  google_calendar: {
    tool: {
      title: 'Google Calendar',
      color: 'rgba(86,210,255,0.30)',
      icon: 'google_calendar.png',
      category: 'calendar',
    },
  },
  google_calendar_web: {
    tool: {
      title: 'Google Calendar (Web)',
      domains: ['calendar.google.com'],
      color: 'rgba(86,210,255,0.30)',
      icon: 'google_calendar.png',
      category: 'calendar',
    },
  },
  google_docs: {
    tool: {
      title: 'Google Docs',
      color: 'rgba(248,81,110,0.30)',
      domains: ['docs.google.com'],
      icon: 'https://icons.timelyapp.com/favicons/docs.google.com',
      category: 'word_processing',
    },
  },
  google_drive: {
    tool: {
      title: 'Google Drive',
      color: 'rgba(248,81,110,0.30)',
      domains: ['drive.google.com'],
      icon: 'https://icons.timelyapp.com/favicons/drive.google.com',
      category: 'storage',
    },
  },
  google_chrome: {
    tool: {
      title: 'Google Chrome',
      aliases: ['Chrome'],
      color: 'rgba(248,81,110,0.30)',
      icon: 'google_chrome.png',
      category: 'browser',
    },
  },
  google_meet: {
    tool: {
      title: 'Google Meet',
      color: 'rgba(248,81,110,0.30)',
      domains: ['meet.google.com'],
      icon: 'https://icons.timelyapp.com/favicons/meet.google.com',
      category: 'video_conferencing',
    },
  },
  youtube: {
    tool: {
      title: 'YouTube',
      color: 'rgba(248,81,110,0.30)',
      domains: ['youtube.com'],
      icon: 'https://icons.timelyapp.com/favicons/youtube.com',
      category: 'video',
    },
  },
  safari: {
    tool: {
      title: 'Safari',
      color: 'rgba(52,166,255,0.30)',
      icon: 'safari.png',
      category: 'browser',
    },
  },
  vivaldi: {
    tool: {
      title: 'Vivaldi',
      color: 'rgba(255, 51, 51, 0.44)',
      icon: 'vivaldi.png',
      category: 'browser',
    },
  },
  brave: {
    tool: {
      title: 'Brave',
      aliases: ['Brave Browser', 'Brave Browser Beta'],
      color: 'rgba(86,210,255,0.30)',
      icon: 'brave.png',
      category: 'browser',
    },
  },
  firefox: {
    tool: {
      title: 'Firefox',
      aliases: ['Mozilla Firefox', 'Firefox Developer Edition'],
      color: 'rgba(255,111,194,0.30)',
      icon: 'firefox.png',
      category: 'browser',
    },
  },

  opera_gx: {
    tool: {
      title: 'Opera GX',
      color: 'rgba(255,0,0,0.30)',
      category: 'browser',
    },
  },
  tor_browser: {
    tool: {
      title: 'Tor Browser',
      color: 'rgba(86,152,219,0.30)',
      category: 'browser',
    },
  },
  yandex_browser: {
    tool: {
      title: 'Yandex Browser',
      color: 'rgba(255,204,0,0.30)',
      category: 'browser',
    },
  },
  pale_moon: {
    tool: {
      title: 'Pale Moon',
      color: 'rgba(0,51,153,0.30)',
      category: 'browser',
    },
  },
  waterfox: {
    tool: {
      title: 'Waterfox',
      color: 'rgba(0,102,204,0.30)',
      category: 'browser',
    },
  },
  chatgpt: {
    tool: {
      title: 'ChatGPT',
      domains: ['chat.openai.com', 'chatgpt.com'],
      color: 'rgba(255,111,194,0.30)',
      category: 'developer_tools',
    },
  },
  claude: {
    tool: {
      title: 'Claude',
      domains: ['claude.ai'],
      color: 'rgba(216,119,87,0.30)',
      category: 'developer_tools',
      icon: 'https://icons.timelyapp.com/apps/mac/Claude?size=64',
    },
  },
  claude_code: {
    tool: {
      title: 'Claude Code',
      aliases: ['Claude Code CLI'],
      color: 'rgba(216,119,87,0.30)',
      category: 'developer_tools',
      icon: 'https://icons.timelyapp.com/apps/mac/Claude?size=64',
    },
  },
  claude_cowork: {
    tool: {
      title: 'Claude Cowork',
      aliases: ['Claude Desktop Cowork'],
      color: 'rgba(216,119,87,0.30)',
      category: 'developer_tools',
      icon: 'https://icons.timelyapp.com/apps/mac/Claude?size=64',
    },
  },
  codex: {
    tool: {
      title: 'Codex',
      aliases: ['Codex CLI'],
      color: 'rgba(86,210,255,0.30)',
      category: 'developer_tools',
    },
  },
  gemini: {
    tool: {
      title: 'Gemini',
      aliases: ['Gemini CLI'],
      color: 'rgba(74,144,226,0.30)',
      category: 'developer_tools',
      icon: 'https://icons.timelyapp.com/apps/mac/Gemini?size=64',
    },
  },
  opera: {
    tool: {
      title: 'Opera',
      color: 'rgba(255,111,194,0.30)',
      icon: 'opera.png',
      category: 'browser',
    },
  },
  xcode: {
    tool: {
      title: 'Xcode',
      color: 'rgba(184,129,243,0.30)',
      icon: 'xcode.png',
      category: 'developer_tools',
    },
  },
  airmail: {
    tool: {
      title: 'Airmail',
      color: 'rgba(86,210,255,0.30)',
      icon: 'airmail.png',
      category: 'communication',
    },
  },
  spark: {
    tool: {
      title: 'Spark',
      color: 'rgba(86,210,255,0.30)',
      icon: 'spark.png',
      category: 'communication',
    },
  },
  coda: {
    tool: {
      title: 'Coda',
      color: 'rgba(137,202,38,0.30)',
      icon: 'coda.png',
      category: 'developer_tools',
    },
  },
  itunes: {
    tool: {
      title: 'iTunes',
      color: 'rgba(248,81,110,0.30)',
      icon: 'itunes.png',
      category: 'music',
    },
  },
  notes: {
    tool: {
      title: 'Notes',
      color: 'rgba(253,251,118,0.30)',
      icon: 'notes.png',
      category: 'notes',
    },
  },
  final_cut_pro: {
    tool: {
      title: 'Final Cut Pro',
      color: 'rgba(248,81,110,0.30)',
      icon: 'finalcutprox.png',
      category: 'media_editing',
    },
  },
  fork: {
    tool: {
      title: 'Fork',
      color: 'rgba(248,81,110,0.30)',
      category: 'developer_tools',
    },

    patterns: [
      // Removes the "*" (indicating uncommitted changes) from titles
      {
        in: 'title',
        pattern: '(.+?)\\*? - Fork',
        mapsTo: ['repo'],
        niceTitle: '{repo}',
      },
    ],
  },
  freshteam: {
    tool: {
      title: 'Freshteam',
      domains: ['*.freshteam.com'],
      color: 'rgba(248,81,110,0.30)',
      category: 'hr',
    },
  },
  timely: {
    tool: {
      title: 'Timely',
      domains: ['*.timelyapp.com', '*.betimely.com'],
      color: 'rgba(25,210,253,0.30)',
      icon: 'timely.png',
      category: 'timetracking',
    },
    patterns: [
      {
        in: 'title',
        pattern: '(.+) - Timely',
        mapsTo: ['page'],
        niceTitle: '{page}',
      },
    ],
  },
  skype: {
    tool: {
      title: 'Skype',
      color: 'rgba(86,210,255,0.30)',
      domains: ['web.skype.com'],
      icon: 'skype.png',
      category: 'video_conferencing',
    },
  },
  calendar: {
    tool: {
      title: 'Calendar',
      aliases: ['Kalendar'],
      color: 'rgba(86,210,255,0.30)',
      icon: 'calendar.png',
      category: 'calendar',
    },
  },
  emacs: {
    tool: {
      title: 'Emacs',
      color: 'rgba(86,210,255,0.30)',
      icon: 'emacs.png',
      category: 'developer_tools',
    },
  },
  github_desktop: {
    tool: {
      title: 'GitHub Desktop',
      color: 'rgba(86,210,255,0.30)',
      icon: 'github.png',
      category: 'developer_tools',
    },
  },
  sourcetree: {
    tool: {
      title: 'SourceTree',
      color: 'rgba(86,210,255,0.30)',
      icon: 'github.png',
      category: 'developer_tools',
    },
  },
  messages: {
    tool: {
      title: 'Messages',
      color: 'rgba(86,210,255,0.30)',
      icon: 'messages.png',
      category: 'communication',
    },
  },
  iterm_2: {
    tool: {
      title: 'iTerm 2',
      aliases: ['iTerm', 'iTerm2'],
      color: 'rgba(86,210,255,0.30)',
      icon: 'iterm2.png',
      category: 'utilities',
    },
  },
  mail: {
    tool: {
      title: 'Mail',
      color: 'rgba(86,210,255,0.30)',
      icon: 'mail.png',
      category: 'communication',
    },
  },
  notion: {
    tool: {
      title: 'Notion',
      color: 'rgba(86,210,255,0.30)',
      icon: 'notion.png',
      category: 'project_management',
    },
  },
  sequel_pro: {
    tool: {
      title: 'Sequel Pro',
      color: 'rgba(86,210,255,0.30)',
      icon: 'sequelpro.png',
      category: 'developer_tools',
    },
  },
  spotify: {
    tool: {
      title: 'Spotify',
      color: 'rgba(86,210,255,0.30)',
      icon: 'spotify.png',
      category: 'music',
    },
  },
  sublime_text: {
    tool: {
      title: 'Sublime Text',
      aliases: [
        'Sublime Text 1.4 (UNREGISTERED)',
        'Sublime Text 2 (UNREGISTERED)',
        'Sublime Text 3 (UNREGISTERED)',
      ],
      color: 'rgba(86,210,255,0.30)',
      icon: 'sublime_text.png',
      category: 'developer_tools',
    },
  },
  visual_studio_code: {
    tool: {
      title: 'Visual Studio Code',
      aliases: ['Code', 'Code - Insiders'],
      color: 'rgba(86,210,255,0.30)',
      icon: 'visual_studio_code.png',
      category: 'developer_tools',
    },
    patterns: [
      {
        in: 'title',
        pattern: '^(.+) — (.+?)(?: \\((.+)\\))?$',
        mapsTo: ['file', 'project', 'workspace'],
        niceTitle: '{file}',
        context: '{project}',
      },
    ],
  },
  cursor: {
    tool: {
      title: 'Cursor', // VSCode clone with AI superpowers
      color: 'rgba(86,210,255,0.30)',
      icon: 'cursor.png',
      category: 'developer_tools',
    },
    patterns: [
      {
        in: 'title',
        pattern: '^(.+) — (.+?)(?: \\((.+)\\))?$',
        mapsTo: ['file', 'project', 'workspace'],
        niceTitle: '{file}',
        context: '{project}',
      },
    ],
  },
  atom: {
    tool: {
      title: 'Atom',
      color: 'rgba(86,210,255,0.30)',
      icon: 'atom.png',
      category: 'developer_tools',
    },
  },
  terminal: {
    tool: {
      title: 'Terminal',
      color: 'rgba(86,210,255,0.30)',
      icon: 'terminal.png',
      category: 'utilities',
    },
  },
  timely_integration_cloud: {
    tool: {
      title: 'Timely Integration Cloud',
      aliases: ['Dewo Cloud'],
      color: 'rgba(86,210,255,0.30)',
      domains: ['integration.timelyapp.com'],
      category: 'developer_tools',
    },
  },
  twitter: {
    tool: {
      title: 'Twitter',
      color: 'rgba(86,210,255,0.30)',
      domains: ['twitter.com'],
      icon: 'twitter.png',
      category: 'social',
    },
  },
  tweetbot: {
    tool: {
      title: 'Tweetbot',
      color: 'rgba(86,210,255,0.30)',
      icon: 'tweetbot.png',
      category: 'social',
    },
  },
  trello: {
    tool: {
      title: 'Trello',
      domains: [
        'trello.com',
        'api.trello.com',
        'a.trellocdn.com',
        '*.trello.com', // Covers admin, blog, and other subdomains
      ],
      color: 'rgba(0,121,191,0.30)', // Trello's official blue color
      category: 'project_management',
      icon: 'https://icons.timelyapp.com/favicons/trello.com',
    },
    patterns: [
      {
        // Board pages: "My Project Board | Trello"
        in: 'title',
        pattern: '^(.+?)\\s*\\|\\s*Trello(?:\\s*\\|\\s*Atlassian Support)?$',
        mapsTo: ['boardName'],
        niceTitle: '{boardName}',
      },
      {
        // Extract board info from URLs for linking to TIC data
        in: 'url',
        pattern: 'https://trello\\.com/b/([a-zA-Z0-9]+)(?:/(.+?))?',
        mapsTo: ['boardId', 'boardName'],
        uri: 'https://api.trello.com/1/boards/{boardId}',
      },
      {
        // Extract card info from URLs for linking to TIC data
        in: 'url',
        pattern: 'https://trello\\.com/c/([a-zA-Z0-9]+)(?:/(.+?))?',
        mapsTo: ['cardId', 'cardName'],
        uri: 'https://api.trello.com/1/cards/{cardId}',
      },
    ],
  },
  todoist: {
    tool: {
      title: 'Todoist',
      color: 'rgba(86,210,255,0.30)',
      icon: 'todoist.png',
      category: 'project_management',
    },
  },
  lightroom: {
    tool: {
      title: 'Lightroom',
      aliases: ['Lightroom CC', 'Adobe Lightroom', 'Adobe Lightroom CC'],
      color: 'rgba(86,210,255,0.30)',
      icon: 'adobe_lightroom.png',
      category: 'media_editing',
    },
  },
  linear: {
    tool: {
      title: 'Linear',
      domains: ['linear.app'],
      color: 'rgba(86,210,255,0.30)',
      category: 'project_management',
      icon: 'https://icons.timelyapp.com/favicons/linear.app',
    },
    patterns: [
      {
        // Issue pages: "Issue title - ENG-123 - Linear"
        in: 'title',
        pattern: '^(.+?)\\s*[-–]\\s*([A-Z]+-\\d+)\\s*[-–]\\s*Linear$',
        mapsTo: ['issueTitle', 'issueIdentifier'],
        niceTitle: '{issueTitle} ({issueIdentifier})',
        context: '{issueIdentifier}',
      },
      {
        // Issue URL: https://linear.app/{workspace}/issue/{TEAM-123}[/slug]
        in: 'url',
        pattern: 'https://linear\\.app/[^/]+/issue/([A-Z]+-\\d+)(?:/[^/]*)?',
        mapsTo: ['issueIdentifier'],
      },
      {
        // Project pages: "Project Name - Linear"
        in: 'title',
        pattern: '^(.+?)\\s*[-–]\\s*Linear$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
      {
        // Project URL: https://linear.app/{workspace}/project/{uuid}[/slug]
        // Project URLs contain UUIDs, so we can generate refs directly.
        in: 'url',
        pattern: 'https://linear\\.app/[^/]+/project/([a-f0-9-]+)(?:/[^/]*)?',
        mapsTo: ['projectId'],
        uri: 'tic://synthetic/linear/projects/{projectId}',
        level: 'project',
      },
    ],
  },
  monday: {
    tool: {
      title: 'Monday.com',
      domains: ['monday.com', '*.monday.com'],
      color: 'rgba(255,51,102,0.30)',
      category: 'project_management',
      icon: 'https://icons.timelyapp.com/favicons/monday.com',
    },
    patterns: [
      {
        // Item pages: "Item Name - Board Name | Monday.com Work Management"
        // or "Item Name | Monday.com Work Management"
        in: 'title',
        pattern: '^(.+?)(?:\\s*[-–]\\s*(.+?))?\\s*\\|\\s*[Mm]onday(?:\\.com)?.*$',
        mapsTo: ['itemOrBoardName', 'boardName'],
        niceTitle: '{itemOrBoardName}',
        context: '{boardName}',
      },
      {
        // Board URL: https://{workspace}.monday.com/boards/{boardId}
        // or https://view.monday.com/board/{boardId}
        in: 'url',
        pattern: 'https://[^/]+\\.monday\\.com/boards/(\\d+)(?:/[^/]*)?',
        mapsTo: ['boardId'],
        uri: 'tic://synthetic/monday/boards/{boardId}',
      },
      {
        // Item URL within board: https://{workspace}.monday.com/boards/{boardId}/pulses/{itemId}
        in: 'url',
        pattern: 'https://[^/]+\\.monday\\.com/boards/(\\d+)/pulses/(\\d+)',
        mapsTo: ['boardId', 'itemId'],
        uri: 'tic://synthetic/monday/items/{itemId}',
      },
      {
        // Shared view URL: https://view.monday.com/{viewId}/{boardId}
        in: 'url',
        pattern: 'https://view\\.monday\\.com/[^/]+/(\\d+)',
        mapsTo: ['boardId'],
        uri: 'tic://synthetic/monday/boards/{boardId}',
      },
    ],
  },
  indesign: {
    tool: {
      title: 'InDesign',
      aliases: ['InDesign CC', 'Adobe InDesign', 'Adobe InDesign CC'],
      color: 'rgba(86,210,255,0.30)',
      icon: 'adobe_indesign.png',
      category: 'design',
    },
  },
  jira: {
    tool: {
      title: 'Jira',
      domains: ['*.atlassian.net', 'jira.atlassian.com'],
      color: 'rgba(86,210,255,0.30)',
      category: 'project_management',
      icon: assets.icons['jira_128.png'],
    },
  },
  premiere_pro: {
    tool: {
      title: 'Premiere Pro',
      aliases: [
        'Premiere Pro CC',
        'Adobe Premiere Pro',
        'Adobe Premiere Pro CC',
        'Adobe Premiere Pro 2024',
        'Adobe Premiere Pro 2025',
      ],
      color: 'rgba(153,0,255,0.30)',
      category: 'video',
      coerceTitle: true,
    },
    patterns: [
      {
        in: 'title',
        pattern:
          '^(?:Adobe )?Premiere Pro(?: CC)?(?: \\d{4})?(?:\\s*-\\s*(?:.*\\\\)?(.*?\\.prproj))?\\s*(\\*)?$',
        mapsTo: ['file', 'modified'],
        niceTitle: '{file}',
      },
      {
        in: 'title',
        pattern: '^(.+?)(?:\\s+-\\s+(?:Adobe )?Premiere Pro(?: CC)?(?: \\d{4})?)?\\s*(\\*)?$',
        mapsTo: ['content', 'modified'],
        niceTitle: '{content}',
      },
    ],
  },
  postico: {
    tool: {
      title: 'Postico',
      color: 'rgba(86,210,255,0.30)',
      category: 'developer_tools',
    },
  },
  dreamweaver: {
    tool: {
      title: 'Dreamweaver',
      color: 'rgba(86,210,255,0.30)',
      icon: 'adobe_dreamweaver.png',
      category: 'developer_tools',
    },
  },
  db_browser_sqlite: {
    tool: {
      title: 'DB Browser for SQLite',
      color: 'rgba(86,210,255,0.30)',
      category: 'developer_tools',
    },
  },
  experience_design: {
    tool: {
      title: 'Experience Design',
      aliases: ['Adobe XD'],
      color: 'rgba(86,210,255,0.30)',
      icon: 'adobe_experiencedesign.png',
    },
  },
  bear: {
    tool: {
      title: 'Bear',
      color: 'rgba(219,66,59,0.30)',
      icon: 'bear.png',
      category: 'notes',
    },
  },
  illustrator: {
    tool: {
      title: 'Illustrator',
      aliases: ['Illustrator CC', 'Adobe Illustrator', 'Adobe Illustrator CC'],
      color: 'rgba(86,210,255,0.30)',
      icon: 'adobe_illustrator.png',
      category: 'design',
    },
  },

  adobe_acrobat: {
    tool: {
      title: 'Adobe Acrobat',
      aliases: [
        'Adobe Acrobat Pro',
        'Adobe Acrobat Reader',
        'Adobe Acrobat Pro (32-bit)',
        'Adobe Acrobat Pro (64-bit)',
        'Adobe Acrobat Reader (32-bit)',
        'Adobe Acrobat Reader (64-bit)',
      ],
      color: 'rgba(255,0,0,0.30)',
      icon: 'adobe_acrobat.png',
      category: 'utilities',
      // coerceTitle: true,
    },
    patterns: [
      {
        in: 'title',
        pattern:
          '^(?:.*\\\\)?([^\\\\]+?)(?:\\s+\\(SECURED\\))?(?:\\s+-\\s+Adobe Acrobat(?:\\s+(?:Pro|Reader))?(?:\\s+\\((?:32|64)-bit\\))?)?\\s*(?:\\(Not Responding\\))?\\s*$',
        mapsTo: ['content'],
        niceTitle: '{content}',
      },
    ],
  },
  after_effects: {
    tool: {
      title: 'After Effects',
      aliases: ['After Effects CC', 'Adobe After Effects', 'Adobe After Effects CC'],
      color: 'rgba(86,210,255,0.30)',
      icon: 'adobe_aftereffects.png',
      category: 'design',
    },
  },
  animate: {
    tool: {
      title: 'Animate',
      aliases: ['Animate CC', 'Adobe Animate', 'Adobe Animate CC'],
      category: 'design',
    },
  },
  discord: {
    tool: {
      title: 'Discord',
      color: 'rgba(114, 137, 218, .3)',
      icon: 'discord.png',
      category: 'communication',
    },
  },
  vmware_fusion: {
    tool: {
      title: 'VMWare Fusion',
      color: 'rgba(209, 34, 18, .3)',
      icon: 'vmware_fusion.png',
      category: 'utilities',
    },
  },
  rubymine: {
    tool: {
      title: 'RubyMine',
      color: 'rgba(255, 39, 79, .3)',
      icon: 'rubymine.png',
      category: 'developer_tools',
    },
  },
  soulver: {
    tool: {
      title: 'Soulver',
      color: 'rgba(253,251,118,0.30)',
      icon: 'soulver.png',
      category: 'productivity',
    },
  },
  facetime: {
    tool: {
      title: 'FaceTime',
      color: 'rgba(245, 179, 137, 0.3)',
      icon: 'facetime.png',
      category: 'video_conferencing',
    },
  },
  finder: {
    tool: {
      title: 'Finder',
      color: 'rgba(245, 179, 137, 0.3)',
      icon: 'finder.png',
      category: 'utilities',
    },
  },
  preview: {
    tool: {
      title: 'Preview',
      color: 'rgba(245, 179, 137, 0.3)',
      icon: 'preview.png',
      category: 'utilities',
    },
  },
  basecamp: {
    tool: {
      title: 'Basecamp',
      aliases: ['Basecamp 3'],
      domains: ['3.basecamp.com', 'basecamp.com'],
      color: 'rgba(245, 179, 137, 0.3)',
      icon: 'https://icons.timelyapp.com/favicons/3.basecamp.com',
    },
  },
  pycharm: {
    tool: {
      title: 'PyCharm',
      aliases: ['PyCharm CE'],
      color: 'rgba(245, 120, 45, 0.3)',
      icon: 'pycharm.png',
      category: 'developer_tools',
    },
  },
  eclipse: {
    tool: {
      title: 'Eclipse',
      aliases: ['Eclipse IDE'],
      color: 'rgba(209, 189, 255, 0.3)',
      icon: 'eclipse.png',
      category: 'developer_tools',
    },
  },
  command_prompt: {
    tool: {
      title: 'Command Prompt',
      aliases: ['Windows Command Processor'],
      color: 'rgba(205, 204, 207, 0.3)',
      icon: 'command_prompt.png',
    },
  },
  docker: {
    tool: {
      title: 'Docker',
      aliases: ['Docker Desktop'],
      color: 'rgba(85, 85, 85, 0.3)',
      icon: 'docker.png',
      category: 'developer_tools',
    },
  },
  pitch: {
    tool: {
      title: 'Pitch',
      domains: ['app.pitch.com'],
      color: 'rgba(51, 74, 192, 0.3)',
      icon: 'https://icons.timelyapp.com/favicons/app.pitch.com',
      category: 'presentation',
    },
  },
  figma: {
    tool: {
      title: 'Figma',
      domains: ['figma.com'],
      color: 'rgba(85, 81, 255,0.3)',
      icon: 'figma.png',
      category: 'design',
    },
  },
  obsidian: {
    tool: {
      title: 'Obsidian',
      color: 'rgb(70, 43, 158, 0.3)',
      icon: 'obsidian.png',
      category: 'knowledge_base',
    },
  },
  loom: {
    tool: {
      title: 'Loom',
      domains: ['loom.com'],
      color: 'rgb(98, 43, 245, 0.3)',
      icon: 'https://icons.timelyapp.com/favicons/loom.com',
      category: 'video',
    },
  },
  zendesk: {
    tool: {
      title: 'Zendesk',
      color: 'rgb(98, 43, 245, 0.3)',
      icon: 'https://icons.timelyapp.com/favicons/zendesk.com',
      category: 'support',
    },
  },
  reddit: {
    tool: {
      title: 'Reddit',
      color: 'rgb(98, 43, 245, 0.3)',
      icon: 'https://icons.timelyapp.com/favicons/reddit.com',
      category: 'social',
    },
  },
  zoom: {
    tool: {
      title: 'Zoom',
      aliases: ['zoom.us', 'Zoom Meeting', 'Video Preview'],
      // Note to self: memory-ai.zoom.us via Safari isn't picked up as `Zoom`.
      // Probably because we're not handling subdomains /w value properly?
      domains: ['zoom.us'],
      coerceTitle: true,
      color: 'rgb(98, 43, 245, 0.3)',
      icon: 'zoom.png',
      category: 'video_conferencing',
    },
  },
};
